import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/base/Loading";
import {
  REPORT_CATEGORIES,
  REPORT_CATEGORY_I18N_KEY,
  type LinkReport,
  type RunnableReport,
} from "@/lib/reportCatalog";
import { HEALTH_WINDOWS, type HealthWindow } from "@/lib/webhookHealth";

const CATEGORY_TONE: Record<string, string> = {
  许可证: "bg-primary-100 text-primary-700",
  激活码: "bg-accent-100 text-accent-700",
  客户: "bg-secondary-100 text-secondary-900",
  审计: "bg-primary-100 text-primary-700",
  监控: "bg-accent-100 text-accent-800",
  批量: "bg-secondary-100 text-secondary-900",
};

function categoryTone(category: string): string {
  return CATEGORY_TONE[category] ?? "bg-background-200 text-foreground-700";
}

interface ReportCatalogProps {
  reports: RunnableReport[];
  linkReports: LinkReport[];
  loading: boolean;
  runningKey: string | null;
  onRun: (report: RunnableReport) => void;
  // 健康快照报表卡片内嵌的时间窗口：无需回到顶部全局选择器即可切换。
  healthWindow: HealthWindow;
  onHealthWindowChange: (window: HealthWindow) => void;
}

// 报表目录：把「可直接生成的报表」与「需前往模块的报表」统一收口到一个入口，
// 前者一键生成 CSV，后者给出跳转，避免报表能力散落在各个后台页面里。
export default function ReportCatalog({
  reports,
  linkReports,
  loading,
  runningKey,
  onRun,
  healthWindow,
  onHealthWindowChange,
}: ReportCatalogProps) {
  const { t } = useTranslation();
  const catLabel = (category: string) =>
    REPORT_CATEGORY_I18N_KEY[category] ? t(REPORT_CATEGORY_I18N_KEY[category]) : category;

  const grouped = REPORT_CATEGORIES.map((category) => ({
    category,
    items: reports.filter((report) => report.category === category),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex flex-col gap-6">
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-36 w-full" />
          ))}
        </div>
      ) : (
        grouped.map((group) => (
          <section key={group.category}>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                {t("reports.catalogRunnable", { category: catLabel(group.category) })}
              </h2>
              <span className="text-xs text-foreground-500">
                {t("reports.catalogCount", { count: group.items.length })}
              </span>
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              {group.items.map((report) => {
                const running = runningKey === report.key;
                return (
                  <div
                    key={report.key}
                    className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={`${report.icon} text-xl`} />
                      </span>
                      <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${categoryTone(
                          report.category,
                        )}`}
                      >
                        {catLabel(report.category)}
                      </span>
                    </div>
                    <h3 className="mt-4 font-heading text-sm font-semibold text-foreground-950">{report.name}</h3>
                    <p className="mt-1.5 flex-1 text-xs leading-relaxed text-foreground-600">{report.description}</p>
                    {report.key === "webhook-health" && (
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100 px-2.5 py-1.5">
                        <span className="flex items-center gap-1 text-[10px] text-foreground-500">
                          <span className="flex h-3 w-3 items-center justify-center">
                            <i className="ri-filter-3-line" />
                          </span>
                          {t("reports.healthWindowInlineLabel")}
                        </span>
                        <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-0.5">
                          {HEALTH_WINDOWS.map((window) => (
                            <button
                              key={window}
                              type="button"
                              onClick={() => onHealthWindowChange(window)}
                              className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] transition-colors ${
                                healthWindow === window
                                  ? "bg-primary-500 text-background-50"
                                  : "text-foreground-600 hover:text-foreground-900"
                              }`}
                            >
                              {t(`approvals.healthWindow${window}`)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={loading || running}
                      onClick={() => onRun(report)}
                      className="mt-4 inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className={running ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} />
                      </span>
                      {running ? t("reports.generating") : t("reports.generate")}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      <section>
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-base font-semibold text-foreground-950">{t("reports.catalogLinksTitle")}</h2>
          <span className="text-xs text-foreground-500">{t("reports.catalogLinksSub")}</span>
        </div>
        <div className="mt-3 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <ul className="divide-y divide-background-200">
            {linkReports.map((report) => (
              <li key={report.key} className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-background-100">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className={`${report.icon}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground-950">{report.name}</p>
                    <span
                      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${categoryTone(
                        report.category,
                      )}`}
                    >
                      {catLabel(report.category)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-foreground-500">{report.description}</p>
                </div>
                <Link
                  to={report.to}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
                >
                  {t("reports.catalogGo")}
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}